import {
  CLAUDE_CODE_EFFORT_OPTIONS,
  CODEX_REASONING_EFFORT_OPTIONS,
  type ClaudeCodeEffort,
  type CodexReasoningEffort,
  DEFAULT_MODEL_BY_PROVIDER,
  type ModelSelection,
  type ProviderKind,
  type ProviderModelOptions,
} from "@cmmd-forge/contracts";
import { normalizeModelSlug } from "@cmmd-forge/shared/model";

type LegacyCodexFields = {
  readonly effort?: CodexReasoningEffort;
  readonly codexFastMode?: boolean;
  readonly serviceTier?: string;
};

export function normalizeProviderKind(value: unknown): ProviderKind | null {
  switch (value) {
    case "codex":
    case "claudeAgent":
    case "ollama":
    case "cmmd":
    case "apex":
    case "cursor":
    case "opencode":
      return value;
    default:
      return null;
  }
}

function isCodexReasoningEffort(value: unknown): value is CodexReasoningEffort {
  return CODEX_REASONING_EFFORT_OPTIONS.some((option) => option === value);
}

function isClaudeCodeEffort(value: unknown): value is ClaudeCodeEffort {
  return CLAUDE_CODE_EFFORT_OPTIONS.some((option) => option === value);
}

// fallow-ignore-next-line complexity
export function normalizeProviderModelOptions(
  value: unknown,
  provider?: ProviderKind | null,
  legacy?: LegacyCodexFields,
): ProviderModelOptions | null {
  const candidate = value && typeof value === "object" ? (value as Record<string, unknown>) : null;
  const codexCandidate =
    candidate?.codex && typeof candidate.codex === "object"
      ? (candidate.codex as Record<string, unknown>)
      : null;
  const claudeCandidate =
    candidate?.claudeAgent && typeof candidate.claudeAgent === "object"
      ? (candidate.claudeAgent as Record<string, unknown>)
      : null;

  const codexReasoningEffort: CodexReasoningEffort | undefined = isCodexReasoningEffort(
    codexCandidate?.reasoningEffort,
  )
    ? codexCandidate.reasoningEffort
    : provider === "codex" && isCodexReasoningEffort(legacy?.effort)
      ? legacy.effort
      : undefined;
  const codexFastMode =
    codexCandidate?.fastMode === true
      ? true
      : codexCandidate?.fastMode === false
        ? false
        : (provider === "codex" && legacy?.codexFastMode === true) ||
            (typeof legacy?.serviceTier === "string" && legacy.serviceTier === "fast")
          ? true
          : undefined;
  const codex =
    codexReasoningEffort !== undefined || codexFastMode !== undefined
      ? {
          ...(codexReasoningEffort !== undefined ? { reasoningEffort: codexReasoningEffort } : {}),
          ...(codexFastMode !== undefined ? { fastMode: codexFastMode } : {}),
        }
      : undefined;

  const claudeThinking =
    claudeCandidate?.thinking === true
      ? true
      : claudeCandidate?.thinking === false
        ? false
        : undefined;
  const claudeEffort: ClaudeCodeEffort | undefined = isClaudeCodeEffort(claudeCandidate?.effort)
    ? claudeCandidate.effort
    : undefined;
  const claudeFastMode =
    claudeCandidate?.fastMode === true
      ? true
      : claudeCandidate?.fastMode === false
        ? false
        : undefined;
  const claudeContextWindow =
    typeof claudeCandidate?.contextWindow === "string" && claudeCandidate.contextWindow.length > 0
      ? claudeCandidate.contextWindow
      : undefined;
  const claude =
    claudeThinking !== undefined ||
    claudeEffort !== undefined ||
    claudeFastMode !== undefined ||
    claudeContextWindow !== undefined
      ? {
          ...(claudeThinking !== undefined ? { thinking: claudeThinking } : {}),
          ...(claudeEffort !== undefined ? { effort: claudeEffort } : {}),
          ...(claudeFastMode !== undefined ? { fastMode: claudeFastMode } : {}),
          ...(claudeContextWindow !== undefined ? { contextWindow: claudeContextWindow } : {}),
        }
      : undefined;

  if (!codex && !claude) return null;
  return {
    ...(codex ? { codex } : {}),
    ...(claude ? { claudeAgent: claude } : {}),
  };
}

// fallow-ignore-next-line complexity
export function normalizeComposerModelSelection(
  value: unknown,
  legacy?: {
    readonly provider?: unknown;
    readonly model?: unknown;
    readonly modelOptions?: unknown;
    readonly legacyCodex?: LegacyCodexFields;
  },
): ModelSelection | null {
  const candidate = value && typeof value === "object" ? (value as Record<string, unknown>) : null;
  const provider = normalizeProviderKind(candidate?.provider ?? legacy?.provider);
  if (provider === null) return null;
  const rawModel = candidate?.model ?? legacy?.model;
  if (typeof rawModel !== "string") return null;
  const model = normalizeModelSlug(rawModel, provider);
  if (!model) return null;
  const modelOptions = normalizeProviderModelOptions(
    candidate?.options ? { [provider]: candidate.options } : legacy?.modelOptions,
    provider,
    provider === "codex" ? legacy?.legacyCodex : undefined,
  );
  const options = provider === "codex" ? modelOptions?.codex : modelOptions?.claudeAgent;
  const rawProviderAccountId = candidate?.providerAccountId;
  const providerAccountId =
    typeof rawProviderAccountId === "string" && rawProviderAccountId.trim().length > 0
      ? rawProviderAccountId.trim()
      : undefined;
  return {
    provider,
    model,
    ...(providerAccountId ? { providerAccountId } : {}),
    ...(options ? { options } : {}),
  } as ModelSelection;
}

export function legacySyncModelSelectionOptions(
  modelSelection: ModelSelection | null,
  modelOptions: ProviderModelOptions | null | undefined,
): ModelSelection | null {
  if (modelSelection === null) return null;
  const options = modelOptions?.[modelSelection.provider];
  return {
    provider: modelSelection.provider,
    model: modelSelection.model,
    ...("providerAccountId" in modelSelection && modelSelection.providerAccountId
      ? { providerAccountId: modelSelection.providerAccountId }
      : {}),
    ...(options ? { options } : {}),
  } as ModelSelection;
}

export function legacyMergeModelSelectionIntoProviderModelOptions(
  modelSelection: ModelSelection | null,
  currentModelOptions: ProviderModelOptions | null | undefined,
): ProviderModelOptions | null {
  if (modelSelection?.options === undefined)
    return normalizeProviderModelOptions(currentModelOptions);
  return legacyReplaceProviderModelOptions(
    normalizeProviderModelOptions(currentModelOptions),
    modelSelection.provider,
    modelSelection.options,
  );
}

function legacyReplaceProviderModelOptions(
  currentModelOptions: ProviderModelOptions | null | undefined,
  provider: ProviderKind,
  nextProviderOptions: ProviderModelOptions[ProviderKind] | null | undefined,
): ProviderModelOptions | null {
  const { [provider]: _discardedProviderModelOptions, ...otherProviderModelOptions } =
    currentModelOptions ?? {};
  const normalizedNextProviderOptions = normalizeProviderModelOptions(
    { [provider]: nextProviderOptions },
    provider,
  );
  return normalizeProviderModelOptions({
    ...otherProviderModelOptions,
    ...(normalizedNextProviderOptions ? normalizedNextProviderOptions : {}),
  });
}

export function legacyToModelSelectionByProvider(
  modelSelection: ModelSelection | null,
  modelOptions: ProviderModelOptions | null | undefined,
): Partial<Record<ProviderKind, ModelSelection>> {
  const result: Partial<Record<ProviderKind, ModelSelection>> = {};
  if (modelOptions) {
    for (const provider of ["codex", "claudeAgent"] as const) {
      const options = modelOptions[provider];
      if (options && Object.keys(options).length > 0) {
        result[provider] = {
          provider,
          model:
            modelSelection?.provider === provider
              ? modelSelection.model
              : DEFAULT_MODEL_BY_PROVIDER[provider],
          options,
        };
      }
    }
  }
  if (modelSelection) result[modelSelection.provider] = modelSelection;
  return result;
}
